import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { AxiosInstance } from '../baseUrl';
import Swal from 'sweetalert2';
import {
    startOfMonth, endOfMonth, startOfWeek, endOfWeek,
    eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths,
} from 'date-fns';

const MONTH_NAMES = [
    '', 'Січень','Лютий','Березень','Квітень','Травень','Червень',
    'Липень','Серпень','Вересень','Жовтень','Листопад','Грудень',
];
const MONTH_SHORT = [
    '', 'Січ','Лют','Бер','Кві','Тра','Чер',
    'Лип','Сер','Вер','Жов','Лис','Гру',
];
const ICONS = [
    // Media & tech
    '📺','📱','💻','🎮','🎵','🎧','📷','🎬',
    // Finance
    '💳','💰','🏦','💵','📈','🧾',
    // Home & utilities
    '🏠','⚡','💧','🔥','🌐','🔑','📡',
    // Food & drinks
    '🛒','🍕','🍔','🥗','☕','🍺','🧃',
    // Transport
    '🚗','✈️','🚌','⛽','🚲',
    // Health & beauty
    '💊','🩺','💅','🧴','💇',
    // Family & people
    '👪','👶','❤️','🐾',
    // Clothes & accessories
    '👗','👟','🧥','👜','💼',
    // Education & work
    '📚','🎓','✏️','🖊️',
    // Other
    '🎁','🎉','🌿','📦','🔧','⚙️','🧸',
];
const NOTIFY_OPTIONS = [
    { value: 0, label: 'Без нагадування' },
    { value: 1, label: 'За 1 день' },
    { value: 3, label: 'За 3 дні' },
    { value: 7, label: 'За тиждень' },
];
const miniBtn = {
    background: 'none', border: 'none', color: '#666', cursor: 'pointer',
    fontSize: 22, padding: '0 6px', lineHeight: 1,
};

// ── Searchable category dropdown ────────────────────────────────────────────
const CategorySelect = ({ categories, value, onChange, onCreateNew, allowAll = false }) => {
    const [open,   setOpen]   = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = e => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = (categories || []).filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase())
    );
    const selected = (categories || []).find(c => c.slug === value);

    const pick = slug => { onChange(slug); setOpen(false); setSearch(''); };

    const triggerStyle = {
        width: '100%', background: '#111', border: '1px solid #2e2e2e',
        borderRadius: 8, color: selected ? '#e8e8e8' : '#555',
        fontSize: 13, padding: '8px 12px', cursor: 'pointer',
        textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6,
        outline: 'none',
    };

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(o => !o)} style={triggerStyle}>
                {allowAll && !selected
                    ? <span style={{ color: '#666' }}>Всі категорії</span>
                    : selected
                        ? <><span style={{ fontSize: 15 }}>{selected.icon}</span>{selected.name}</>
                        : <span style={{ color: '#555' }}>Обрати…</span>
                }
                <span style={{ marginLeft: 'auto', color: '#444', fontSize: 10, flexShrink: 0 }}>▾</span>
            </button>

            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, minWidth: '100%',
                    zIndex: 200, background: '#111', border: '1px solid #2e2e2e', borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)', overflow: 'hidden',
                }}>
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #1e1e1e' }}>
                        <input
                            autoFocus
                            style={{
                                width: '100%', background: '#0d0d0d', border: '1px solid #2e2e2e',
                                borderRadius: 6, color: '#e8e8e8', fontSize: 12,
                                padding: '5px 9px', outline: 'none', boxSizing: 'border-box',
                            }}
                            placeholder="Пошук…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div style={{ maxHeight: 210, overflowY: 'auto' }}>
                        {allowAll && (
                            <div
                                onClick={() => pick('')}
                                style={{
                                    padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                    color: !value ? '#e8e8e8' : '#888',
                                    background: !value ? '#1e1e2e' : 'transparent',
                                    borderBottom: '1px solid #1a1a1a',
                                }}
                                onMouseEnter={e => { if (value) e.currentTarget.style.background = '#18181e'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = !value ? '#1e1e2e' : 'transparent'; }}
                            >Всі категорії</div>
                        )}
                        {filtered.map(c => (
                            <div
                                key={c.slug}
                                onClick={() => pick(c.slug)}
                                style={{
                                    padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                    color: c.slug === value ? '#e8e8e8' : '#aaa',
                                    background: c.slug === value ? '#1e1e2e' : 'transparent',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                }}
                                onMouseEnter={e => { if (c.slug !== value) e.currentTarget.style.background = '#18181e'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = c.slug === value ? '#1e1e2e' : 'transparent'; }}
                            >
                                <span style={{ fontSize: 15 }}>{c.icon}</span>{c.name}
                            </div>
                        ))}
                        {filtered.length === 0 && (
                            <div style={{ padding: '8px 12px', color: '#444', fontSize: 12 }}>Не знайдено</div>
                        )}
                        {onCreateNew && (
                            <div
                                onClick={() => { onCreateNew(); setOpen(false); setSearch(''); }}
                                style={{
                                    padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                    color: '#7a8af2', borderTop: '1px solid #1e1e1e',
                                    display: 'flex', alignItems: 'center', gap: 8,
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#18181e'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >＋ Нова категорія</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Payment calendar ────────────────────────────────────────────────────────
const PaymentCalendar = ({ month, year, expenses, onChange }) => {
    const ref  = new Date(year, month - 1, 1);
    const days = eachDayOfInterval({
        start: startOfWeek(startOfMonth(ref), { weekStartsOn: 1 }),
        end:   endOfWeek(endOfMonth(ref),     { weekStartsOn: 1 }),
    });
    const today = new Date();
    const expByDay = {};
    (expenses || []).forEach(e => {
        if (!expByDay[e.day_of_month]) expByDay[e.day_of_month] = [];
        expByDay[e.day_of_month].push(e);
    });
    const prev = () => { const d = subMonths(ref, 1); onChange(d.getMonth() + 1, d.getFullYear()); };
    const next = () => { const d = addMonths(ref, 1); onChange(d.getMonth() + 1, d.getFullYear()); };

    return (
        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <button onClick={prev} style={miniBtn}>‹</button>
                <span style={{ color: '#e8e8e8', fontWeight: 700, fontSize: 15 }}>{MONTH_NAMES[month]} {year}</span>
                <button onClick={next} style={miniBtn}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 6 }}>
                {['Пн','Вт','Ср','Чт','Пт','Сб','Нд'].map(d => (
                    <div key={d} style={{ color: '#444', fontSize: 11, textAlign: 'center', fontWeight: 700, padding: '4px 0' }}>{d}</div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {days.map(day => {
                    const inMonth = isSameMonth(day, ref);
                    const isToday = isSameDay(day, today);
                    const dayExps = inMonth ? (expByDay[day.getDate()] || []) : [];
                    return (
                        <div key={day.toISOString()} style={{
                            minHeight: 72, padding: '6px 6px 4px', borderRadius: 8,
                            background: isToday ? '#5865f215' : (inMonth ? '#141414' : 'transparent'),
                            border: isToday ? '1px solid #5865f244' : '1px solid transparent',
                            opacity: inMonth ? 1 : 0.2,
                        }}>
                            <div style={{
                                width: 22, height: 22, borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                background: isToday ? '#5865f2' : 'transparent',
                                color: isToday ? '#fff' : '#666',
                                fontSize: 12, fontWeight: isToday ? 700 : 400, marginBottom: 4,
                            }}>{day.getDate()}</div>
                            {dayExps.slice(0, 3).map(exp => (
                                <div key={exp.id} style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 2, overflow: 'hidden' }}>
                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: exp.color || '#5865f2', flexShrink: 0, opacity: exp.paid ? 0.4 : 1 }} />
                                    <span style={{ fontSize: 9, color: exp.paid ? '#444' : '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: exp.paid ? 'line-through' : 'none' }}>
                                        {exp.title}
                                    </span>
                                </div>
                            ))}
                            {dayExps.length > 3 && <div style={{ fontSize: 9, color: '#444' }}>+{dayExps.length - 3}</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Category block ──────────────────────────────────────────────────────────
const CategoryBlock = ({ catObj, expenses, month, year, onTogglePaid, onEdit, onDelete, onCatUpdated }) => {
    const items  = [...(expenses || []).filter(e => e.category === catObj.slug)].sort((a, b) => {
        if (a.paid !== b.paid) return a.paid ? -1 : 1;
        return a.day_of_month - b.day_of_month;
    });
    const total  = items.reduce((s, e) => s + e.amount, 0);
    const paidAm = items.filter(e => e.paid).reduce((s, e) => s + (e.amount_paid || 0), 0);

    const [editCat,   setEditCat]   = useState(false);
    const [catName,   setCatName]   = useState(catObj.name);
    const [catColor,  setCatColor]  = useState(catObj.color);
    const [catIcon,   setCatIcon]   = useState(catObj.icon);
    const [savingCat, setSavingCat] = useState(false);

    const saveCatEdit = async () => {
        if (!catName.trim()) return;
        setSavingCat(true);
        try {
            const r = await AxiosInstance.put(`/expense-categories/${catObj.id}`, {
                name: catName.trim(), color: catColor, icon: catIcon,
            });
            onCatUpdated(r.data);
            setEditCat(false);
        } catch {} finally { setSavingCat(false); }
    };

    const inpS = {
        background: '#0d0d0d', border: '1px solid #2e2e2e', borderRadius: 7,
        color: '#e8e8e8', fontSize: 12, padding: '6px 10px', outline: 'none', width: '100%', boxSizing: 'border-box',
    };

    return (
        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '14px 16px', minWidth: 0, boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editCat ? 10 : undefined }}>
                <span style={{ color: '#888', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                        width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                    }}>{catObj.icon}</span>
                    {catObj.name}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#e8e8e8', fontSize: 14, fontWeight: 700 }}>{total.toLocaleString('uk-UA')} ₴</span>
                    <button title="Редагувати категорію" onClick={() => { setEditCat(e => !e); setCatName(catObj.name); setCatColor(catObj.color); setCatIcon(catObj.icon); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: editCat ? '#5865f2' : '#333', fontSize: 13, padding: '0 2px' }}>✎</button>
                </div>
            </div>

            {editCat && (
                <div style={{ background: '#111', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                    <input style={inpS} value={catName} onChange={e => setCatName(e.target.value)} placeholder="Назва" />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}>
                        <input type="color" value={catColor}
                               onChange={e => setCatColor(e.target.value)}
                               onInput={e => setCatColor(e.target.value)}
                               style={{ width: 34, height: 30, border: '1px solid #2e2e2e', borderRadius: 6, background: 'none', cursor: 'pointer', padding: 2 }} />
                        <div style={{ width: 30, height: 30, borderRadius: 6, background: catColor + '22', border: `1px solid ${catColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{catIcon}</div>
                        <span style={{ color: '#444', fontSize: 11 }}>Іконка:</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxHeight: 110, overflowY: 'auto' }}>
                        {ICONS.map(ic => (
                            <button key={ic} type="button" onClick={() => setCatIcon(ic)} style={{
                                width: 30, height: 30, borderRadius: 5,
                                border: `1px solid ${catIcon === ic ? '#5865f2' : '#2e2e2e'}`,
                                background: catIcon === ic ? '#3a40a022' : '#0d0d0d',
                                cursor: 'pointer', fontSize: 14,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>{ic}</button>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button type="button" onClick={saveCatEdit} disabled={savingCat} style={{
                            padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                            background: '#5865f2', color: '#fff', fontSize: 11, fontWeight: 600,
                            opacity: savingCat ? 0.5 : 1,
                        }}>Зберегти</button>
                        <button type="button" onClick={() => setEditCat(false)} style={{
                            padding: '5px 10px', borderRadius: 6, border: '1px solid #2e2e2e',
                            background: 'transparent', color: '#666', fontSize: 11, cursor: 'pointer',
                        }}>Скасувати</button>
                    </div>
                </div>
            )}

            {items.length === 0 ? (
                <div style={{ color: '#333', fontSize: 12 }}>—</div>
            ) : (
                <>
                    <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5, paddingRight: 2 }}>
                        {items.map(exp => (
                            <div key={exp.id} style={{
                                display: 'flex', alignItems: 'center', gap: 7,
                                padding: '6px 9px', borderRadius: 7,
                                background: exp.paid ? '#1e2a1e' : '#181818',
                                border: `1px solid ${exp.paid ? '#2a4a2a' : '#242424'}`,
                            }}>
                                <div style={{ width: 7, height: 7, borderRadius: '50%', background: exp.color || '#5865f2', flexShrink: 0 }} />
                                <span style={{
                                    flex: 1, fontSize: 12,
                                    color: exp.paid ? '#5a7a5a' : '#c8c8c8',
                                    textDecoration: exp.paid ? 'line-through' : 'none',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>{exp.title}</span>
                                <span style={{ fontSize: 11, color: '#555', flexShrink: 0 }}>{exp.amount.toLocaleString('uk-UA')} ₴</span>
                                <span style={{ fontSize: 10, color: '#3a3a3a', flexShrink: 0 }}>{exp.day_of_month} ч.</span>
                                <button
                                    title={exp.paid ? 'Скасувати оплату' : 'Позначити оплаченим'}
                                    onClick={() => onTogglePaid(exp, month, year)}
                                    style={{
                                        background: exp.paid ? '#1e3a1e' : 'transparent',
                                        border: `1px solid ${exp.paid ? '#2a5a2a' : '#2e2e2e'}`,
                                        borderRadius: 4, cursor: 'pointer',
                                        color: exp.paid ? '#5abf5a' : '#444',
                                        fontSize: 12, padding: '1px 5px', flexShrink: 0,
                                    }}>✓</button>
                                <button title="Редагувати" onClick={() => onEdit(exp)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#444', fontSize: 12, padding: '0 3px', flexShrink: 0 }}>✎</button>
                                <button title="Видалити" onClick={() => onDelete(exp)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3a1a1a', fontSize: 12, padding: '0 3px', flexShrink: 0 }}>✕</button>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, fontSize: 11 }}>
                        <span style={{ color: '#4a7a4a' }}>Сплачено {paidAm.toLocaleString('uk-UA')} ₴</span>
                        <span style={{ color: '#333' }}>·</span>
                        <span style={{ color: '#444' }}>Лишилось {(total - paidAm).toLocaleString('uk-UA')} ₴</span>
                    </div>
                </>
            )}
        </div>
    );
};

// ── Inline new-category creator ─────────────────────────────────────────────
const CategoryCreator = ({ onSave, onCancel }) => {
    const [name,   setName]   = useState('');
    const [color,  setColor]  = useState('#5865f2');
    const [icon,   setIcon]   = useState('📦');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            const r = await AxiosInstance.post('/expense-categories', { name: name.trim(), color, icon });
            onSave(r.data);
        } catch {
            // silent
        } finally {
            setSaving(false);
        }
    };

    const inp = {
        background: '#0d0d0d', border: '1px solid #2e2e2e', borderRadius: 8,
        color: '#e8e8e8', fontSize: 13, padding: '7px 11px', outline: 'none',
    };

    return (
        <div style={{ background: '#111', border: '1px solid #383838', borderRadius: 10, padding: '14px 16px', marginTop: 10 }}>
            <div style={{ color: '#888', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
                Нова категорія
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                    style={{ ...inp, flex: 1 }}
                    placeholder="Назва категорії"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && save()}
                />
                <input type="color" value={color}
                       onChange={e => setColor(e.target.value)}
                       onInput={e => setColor(e.target.value)}
                       style={{ width: 36, height: 34, border: '1px solid #2e2e2e', borderRadius: 6, background: 'none', cursor: 'pointer', padding: 2 }} />
                <div style={{
                    width: 36, height: 34, borderRadius: 6, border: `2px solid ${color}44`,
                    background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>{icon}</div>
            </div>
            <div style={{ marginTop: 10 }}>
                <div style={{ color: '#444', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Іконка</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {ICONS.map(ic => (
                        <button key={ic} type="button" onClick={() => setIcon(ic)} style={{
                            width: 34, height: 34, borderRadius: 6,
                            border: `1px solid ${icon === ic ? '#5865f2' : '#2e2e2e'}`,
                            background: icon === ic ? '#3a40a022' : '#111',
                            cursor: 'pointer', fontSize: 16,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{ic}</button>
                    ))}
                </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" onClick={save} disabled={saving || !name.trim()} style={{
                    padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: '#5865f2', color: '#fff', fontSize: 12, fontWeight: 600,
                    opacity: saving || !name.trim() ? 0.5 : 1,
                }}>Зберегти</button>
                <button type="button" onClick={onCancel} style={{
                    padding: '7px 14px', borderRadius: 8, border: '1px solid #2e2e2e',
                    background: 'transparent', color: '#666', fontSize: 12, cursor: 'pointer',
                }}>Скасувати</button>
            </div>
        </div>
    );
};

// ── Quick one-time expense form ─────────────────────────────────────────────
const QuickExpenseForm = ({ categories, onSave, onCategoryCreated }) => {
    const [title,       setTitle]       = useState('');
    const [amount,      setAmount]      = useState('');
    const [color,       setColor]       = useState('#5865f2');
    const [catSlug,     setCatSlug]     = useState(categories[0]?.slug || '');
    const [saving,      setSaving]      = useState(false);
    const [showCreator, setShowCreator] = useState(false);

    useEffect(() => {
        if (!catSlug && categories.length) setCatSlug(categories[0].slug);
    }, [categories]);

    const handleCatChange = e => {
        if (e.target.value === '__new__') {
            setShowCreator(true);
        } else {
            setCatSlug(e.target.value);
            setColor(categories.find(c => c.slug === e.target.value)?.color || color);
            setShowCreator(false);
        }
    };

    const handleNewCat = cat => {
        onCategoryCreated(cat);
        setCatSlug(cat.slug);
        setColor(cat.color);
        setShowCreator(false);
    };

    const handleSubmit = async e => {
        e.preventDefault();
        if (!title.trim() || !amount) return;
        setSaving(true);
        try {
            await onSave({ title: title.trim(), amount: parseInt(amount, 10), color, category: catSlug });
            setTitle('');
            setAmount('');
        } catch {
            // parent handles error
        } finally {
            setSaving(false);
        }
    };

    const inp = {
        background: '#111', border: '1px solid #2e2e2e', borderRadius: 8,
        color: '#e8e8e8', fontSize: 13, padding: '8px 12px', outline: 'none',
    };

    return (
        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
                Швидка витрата — списати зараз
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 180 }}>
                    <CategorySelect
                        categories={categories}
                        value={catSlug}
                        onChange={slug => {
                            setCatSlug(slug);
                            setColor(categories.find(c => c.slug === slug)?.color || color);
                            setShowCreator(false);
                        }}
                        onCreateNew={() => setShowCreator(true)}
                    />
                </div>
                <input
                    style={{ ...inp, flex: 1, minWidth: 160 }}
                    placeholder="Назва (наприклад: Кава)"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    required
                />
                <input
                    style={{ ...inp, width: 120 }}
                    type="number" min="1"
                    placeholder="Сума ₴"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    required
                />
                <input
                    type="color" value={color} onChange={e => setColor(e.target.value)}
                    title="Колір"
                    style={{ width: 36, height: 36, border: '1px solid #2e2e2e', borderRadius: 6, background: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}
                />
                <button type="submit" disabled={saving || !title.trim() || !amount || showCreator} style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: '#27ae60', color: '#fff', fontWeight: 600, fontSize: 13, flexShrink: 0,
                    opacity: saving || !title.trim() || !amount || showCreator ? 0.5 : 1,
                }}>− Списати</button>
            </form>
            {showCreator && (
                <CategoryCreator
                    onSave={handleNewCat}
                    onCancel={() => { setShowCreator(false); setCatSlug(categories[0]?.slug || ''); }}
                />
            )}
        </div>
    );
};

// ── Add/Edit expense form ───────────────────────────────────────────────────
const EMPTY_FORM = {
    title: '', amount: '', color: '#5865f2',
    category: '', day_of_month: 1, repeat_until: '', notify_before: 0,
};

const ExpenseForm = ({ initial, onSave, onCancel, categories, onCategoryCreated }) => {
    const [form,        setForm]        = useState(initial || { ...EMPTY_FORM, category: categories[0]?.slug || '' });
    const [showCreator, setShowCreator] = useState(false);

    useEffect(() => {
        setForm(initial || { ...EMPTY_FORM, category: categories[0]?.slug || '' });
        setShowCreator(false);
    }, [initial]);

    useEffect(() => {
        if (!form.category && categories.length) setForm(f => ({ ...f, category: categories[0].slug }));
    }, [categories]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleCatChange = e => {
        if (e.target.value === '__new__') {
            setShowCreator(true);
        } else {
            set('category', e.target.value);
            setShowCreator(false);
        }
    };

    const handleNewCat = cat => {
        onCategoryCreated(cat);
        set('category', cat.slug);
        setShowCreator(false);
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!form.title.trim() || !form.amount) return;
        onSave({
            ...form,
            amount:        parseInt(form.amount, 10),
            day_of_month:  parseInt(form.day_of_month, 10),
            notify_before: parseInt(form.notify_before, 10),
            repeat_until:  form.repeat_until || null,
        });
    };

    const inp = {
        background: '#111', border: '1px solid #2e2e2e', borderRadius: 8,
        color: '#e8e8e8', fontSize: 13, padding: '8px 12px', width: '100%', outline: 'none',
    };
    const lbl = { color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 };

    const selectedCat = categories.find(c => c.slug === form.category);

    return (
        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '20px 24px' }}>
            <div style={{ color: '#e8e8e8', fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
                {initial ? 'Редагувати витрату' : 'Нова витрата'}
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    <div>
                        <div style={lbl}>Назва</div>
                        <input style={inp} value={form.title} onChange={e => set('title', e.target.value)} placeholder="Наприклад: Netflix" required />
                    </div>
                    <div>
                        <div style={lbl}>Сума (₴)</div>
                        <input style={inp} type="number" min="1" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="450" required />
                    </div>
                    <div>
                        <div style={lbl}>День місяця</div>
                        <input style={inp} type="number" min="1" max="31" value={form.day_of_month} onChange={e => set('day_of_month', e.target.value)} />
                    </div>
                    <div style={{ gridColumn: showCreator ? '1 / -1' : undefined }}>
                        <div style={lbl}>Категорія</div>
                        <CategorySelect
                            categories={categories}
                            value={form.category}
                            onChange={slug => { set('category', slug); setShowCreator(false); }}
                            onCreateNew={() => setShowCreator(true)}
                        />
                        {showCreator && (
                            <CategoryCreator
                                onSave={handleNewCat}
                                onCancel={() => {
                                    setShowCreator(false);
                                    set('category', categories[0]?.slug || '');
                                }}
                            />
                        )}
                    </div>
                    {!showCreator && (
                        <>
                            <div>
                                <div style={lbl}>Колір</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
                                           style={{ width: 38, height: 36, border: '1px solid #2e2e2e', borderRadius: 6, background: 'none', cursor: 'pointer', padding: 2 }} />
                                    <input style={{ ...inp, flex: 1 }} value={form.color} onChange={e => set('color', e.target.value)} placeholder="#5865f2" />
                                </div>
                            </div>
                            <div>
                                <div style={lbl}>Повторювати до</div>
                                <input style={inp} type="date" value={form.repeat_until || ''} onChange={e => set('repeat_until', e.target.value)} />
                                <div style={{ color: '#3a3a3a', fontSize: 10, marginTop: 2 }}>не заповнювати = щомісяця назавжди</div>
                            </div>
                        </>
                    )}
                </div>

                {!showCreator && (
                    <div>
                        <div style={lbl}>Нагадати</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {NOTIFY_OPTIONS.map(opt => (
                                <button key={opt.value} type="button" onClick={() => set('notify_before', opt.value)} style={{
                                    padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                                    border: '1px solid',
                                    borderColor: form.notify_before === opt.value ? '#5865f2' : '#2e2e2e',
                                    background: form.notify_before === opt.value ? '#3a40a022' : '#111',
                                    color: form.notify_before === opt.value ? '#8891f2' : '#555',
                                }}>{opt.label}</button>
                            ))}
                        </div>
                    </div>
                )}

                {!showCreator && (
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button type="submit" style={{
                            padding: '9px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: '#5865f2', color: '#fff', fontWeight: 600, fontSize: 13,
                        }}>Зберегти</button>
                        {initial && (
                            <button type="button" onClick={onCancel} style={{
                                padding: '9px 20px', borderRadius: 8, border: '1px solid #2e2e2e',
                                cursor: 'pointer', background: 'transparent', color: '#666', fontSize: 13,
                            }}>Скасувати редагування</button>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
};

// ── Donut / bar range chart ─────────────────────────────────────────────────
const DonutSection = ({ expenses, categories }) => {
    const [filterCat, setFilterCat] = useState('');
    const [fromMonth, setFromMonth] = useState('');
    const [fromYear,  setFromYear]  = useState('');
    const [toMonth,   setToMonth]   = useState('');
    const [toYear,    setToYear]    = useState('');
    const [rangeData, setRangeData] = useState([]);
    const [barMode,   setBarMode]   = useState(false);
    const [loading,   setLoading]   = useState(false);

    const hasRange = fromMonth && fromYear && toMonth && toYear;

    useEffect(() => {
        if (!hasRange) {
            setBarMode(false);
            setRangeData([]);
            return;
        }
        setLoading(true);
        const params = {
            from_month: parseInt(fromMonth),
            from_year:  parseInt(fromYear),
            to_month:   parseInt(toMonth),
            to_year:    parseInt(toYear),
        };
        if (filterCat) params.category = filterCat;
        AxiosInstance.get('/expenses/summary', { params })
            .then(r => { setRangeData(r.data); setBarMode(true); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [hasRange, fromMonth, fromYear, toMonth, toYear, filterCat]);

    const reset = () => {
        setFilterCat('');
        setFromMonth('');
        setFromYear('');
        setToMonth('');
        setToYear('');
        setRangeData([]);
        setBarMode(false);
    };

    const donutOption = useMemo(() => {
        const slices = (categories || []).map(cat => ({
            name: `${cat.icon} ${cat.name}`,
            value: (expenses || []).filter(e => e.category === cat.slug && e.paid).reduce((s, e) => s + (e.amount_paid || 0), 0),
            itemStyle: { color: cat.color },
        })).filter(d => d.value > 0);
        if (!slices.length) return null;
        return {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                backgroundColor: '#1e1e1e', borderColor: '#2e2e2e',
                textStyle: { color: '#e8e8e8', fontSize: 12 },
                formatter: p => `${p.name}<br/><b>${p.value.toLocaleString('uk-UA')} ₴</b> (${p.percent}%)`,
            },
            legend: {
                orient: 'vertical', right: '2%', top: 'center',
                textStyle: { color: '#666', fontSize: 12 },
            },
            series: [{
                type: 'pie', radius: ['42%', '68%'], center: ['35%', '50%'],
                data: slices,
                label: { show: false },
                emphasis: {
                    label: { show: true, fontSize: 13, fontWeight: 'bold', color: '#e8e8e8' },
                    itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.5)' },
                },
            }],
        };
    }, [expenses, categories]);

    const barOption = useMemo(() => {
        if (!rangeData.length || !fromMonth || !fromYear || !toMonth || !toYear) return null;
        const months = [];
        let m = parseInt(fromMonth), y = parseInt(fromYear);
        const toM = parseInt(toMonth), toY = parseInt(toYear);
        while (y < toY || (y === toY && m <= toM)) {
            months.push({ month: m, year: y });
            m++; if (m > 12) { m = 1; y++; }
        }
        const catsToShow = filterCat
            ? (categories || []).filter(c => c.slug === filterCat)
            : (categories || []);
        const series = catsToShow.map(cat => ({
            name: `${cat.icon} ${cat.name}`,
            type: 'bar', barMaxWidth: 40,
            stack: !filterCat ? 'total' : undefined,
            data: months.map(({ month, year }) => {
                const row = rangeData.find(r => r.month === month && r.year === year && r.category === cat.slug);
                return row?.total || 0;
            }),
            itemStyle: { color: cat.color, borderRadius: [4, 4, 0, 0] },
        }));
        return {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#1e1e1e', borderColor: '#2e2e2e',
                textStyle: { color: '#e8e8e8', fontSize: 12 },
                formatter: params => {
                    const lines = params.filter(p => p.value > 0).map(p =>
                        `<span style="color:${p.color}">■</span> ${p.seriesName}: <b>${p.value.toLocaleString('uk-UA')} ₴</b>`
                    );
                    return `<b>${params[0]?.name}</b><br/>${lines.join('<br/>')}`;
                },
            },
            legend: { textStyle: { color: '#666', fontSize: 12 }, top: 4 },
            grid: { left: 16, right: 16, bottom: 8, top: 44, containLabel: true },
            xAxis: {
                type: 'category',
                data: months.map(({ month, year }) => `${MONTH_SHORT[month]} ${year}`),
                axisLine: { lineStyle: { color: '#2e2e2e' } },
                axisLabel: { color: '#666', fontSize: 11 },
            },
            yAxis: {
                type: 'value',
                axisLine: { lineStyle: { color: '#2e2e2e' } },
                splitLine: { lineStyle: { color: '#1e1e1e' } },
                axisLabel: { color: '#444', fontSize: 10 },
            },
            series,
        };
    }, [rangeData, fromMonth, fromYear, toMonth, toYear, filterCat, categories]);

    const years = [];
    for (let y = 2024; y <= new Date().getFullYear() + 1; y++) years.push(y);

    const sel = {
        background: '#111', border: '1px solid #2e2e2e', borderRadius: 8,
        color: '#e8e8e8', fontSize: 12, padding: '6px 10px', cursor: 'pointer', outline: 'none',
    };
    const showReset = barMode || filterCat || fromMonth || toMonth;

    return (
        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {barMode ? 'Витрати по місяцях' : 'Сплачено по категоріях'}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ minWidth: 180 }}>
                        <CategorySelect
                            categories={categories || []}
                            value={filterCat}
                            onChange={setFilterCat}
                            allowAll
                        />
                    </div>
                    <select style={sel} value={fromMonth} onChange={e => setFromMonth(e.target.value)}>
                        <option value="">Від (міс.)</option>
                        {MONTH_NAMES.slice(1).map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
                    </select>
                    <select style={sel} value={fromYear} onChange={e => setFromYear(e.target.value)}>
                        <option value="">Рік</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <span style={{ color: '#333', fontSize: 14 }}>—</span>
                    <select style={sel} value={toMonth} onChange={e => setToMonth(e.target.value)}>
                        <option value="">До (міс.)</option>
                        {MONTH_NAMES.slice(1).map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
                    </select>
                    <select style={sel} value={toYear} onChange={e => setToYear(e.target.value)}>
                        <option value="">Рік</option>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    {showReset && (
                        <button onClick={reset} style={{
                            padding: '6px 14px', borderRadius: 8,
                            border: '1px solid #2e2e2e', background: 'transparent',
                            color: '#888', fontSize: 12, cursor: 'pointer',
                        }}>Скинути дати</button>
                    )}
                </div>
            </div>

            {loading ? (
                <div style={{ color: '#444', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>Завантаження…</div>
            ) : barMode ? (
                barOption
                    ? <ReactECharts option={barOption} style={{ height: 260 }} />
                    : <div style={{ color: '#444', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>Немає оплат за цей період</div>
            ) : (
                donutOption
                    ? <ReactECharts option={donutOption} style={{ height: 260 }} />
                    : <div style={{ color: '#444', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>Нічого не сплачено цього місяця</div>
            )}
        </div>
    );
};

// ── Summary card ────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, sub, color, highlight }) => (
    <div style={{
        background: highlight || '#1a1a1a',
        border: `1px solid ${highlight ? color + '33' : '#2e2e2e'}`,
        borderRadius: 12, padding: '16px 24px', flex: 1,
    }}>
        <div style={{ color: highlight ? color + '99' : '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
        <div style={{ color, fontSize: 28, fontWeight: 700, marginTop: 4 }}>{value.toLocaleString('uk-UA')} ₴</div>
        <div style={{ color: highlight ? '#777' : '#3a3a3a', fontSize: 11, marginTop: 4 }}>{sub}</div>
    </div>
);

// ── Main page ───────────────────────────────────────────────────────────────
const ExpensesPage = () => {
    const today = new Date();
    const [month,      setMonth]      = useState(today.getMonth() + 1);
    const [year,       setYear]       = useState(today.getFullYear());
    const [data,       setData]       = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [editExp,    setEditExp]    = useState(null);
    const [catSearch,  setCatSearch]  = useState('');

    const loadCategories = useCallback(async () => {
        try {
            const r = await AxiosInstance.get('/expense-categories');
            setCategories(r.data);
        } catch {}
    }, []);

    const load = useCallback(async (m, y) => {
        setLoading(true);
        try {
            const r = await AxiosInstance.get('/expenses', { params: { month: m, year: y } });
            setData(r.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadCategories(); }, [loadCategories]);
    useEffect(() => { load(month, year); }, [month, year, load]);

    const handleMonthChange = (m, y) => { setMonth(m); setYear(y); };
    const handleCatCreated  = cat => setCategories(prev => [...prev, cat]);
    const handleCatUpdated  = updated => setCategories(prev => prev.map(c => c.id === updated.id ? updated : c));

    const handleQuickSave = async ({ title, amount, color, category }) => {
        const today = new Date();
        const dayOfMonth = today.getDate();
        const repeatUntil = today.toISOString().split('T')[0];
        try {
            const res = await AxiosInstance.post('/expenses', {
                title, amount, color, category,
                day_of_month: dayOfMonth,
                repeat_until: repeatUntil,
                notify_before: 0,
            });
            await AxiosInstance.post(`/expenses/${res.data.id}/pay`, null, {
                params: { month, year },
            });
            load(month, year);
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', text: 'Не вдалось зберегти', background: '#1a1a1a', color: '#e8e8e8' });
            throw new Error('failed');
        }
    };

    const handleTogglePaid = async (exp, m, y) => {
        try {
            if (exp.paid) {
                await AxiosInstance.delete(`/expenses/${exp.id}/pay`, { params: { month: m, year: y } });
            } else {
                await AxiosInstance.post(`/expenses/${exp.id}/pay`, null, { params: { month: m, year: y } });
            }
            load(m, y);
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', text: 'Не вдалось оновити статус', background: '#1a1a1a', color: '#e8e8e8' });
        }
    };

    const handleSave = async (formData) => {
        try {
            if (editExp) {
                await AxiosInstance.put(`/expenses/${editExp.id}`, formData);
                setEditExp(null);
            } else {
                await AxiosInstance.post('/expenses', formData);
            }
            load(month, year);
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', text: 'Не вдалось зберегти', background: '#1a1a1a', color: '#e8e8e8' });
        }
    };

    const handleDelete = async (exp) => {
        const res = await Swal.fire({
            title: `Видалити "${exp.title}"?`,
            text: 'Також видалить всі записи оплат.',
            icon: 'warning', showCancelButton: true,
            confirmButtonText: 'Видалити', cancelButtonText: 'Скасувати',
            background: '#1a1a1a', color: '#e8e8e8', confirmButtonColor: '#c0392b',
        });
        if (!res.isConfirmed) return;
        try {
            await AxiosInstance.delete(`/expenses/${exp.id}`);
            load(month, year);
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', text: 'Не вдалось видалити', background: '#1a1a1a', color: '#e8e8e8' });
        }
    };

    // bar chart — current month by category
    const barChartOption = useMemo(() => {
        if (!data?.expenses?.length || !categories.length) return null;
        const cats = categories.filter(c => data.expenses.some(e => e.category === c.slug));
        if (!cats.length) return null;
        const totalData = cats.map(c => data.expenses.filter(e => e.category === c.slug).reduce((s, e) => s + e.amount, 0));
        const paidData  = cats.map(c => data.expenses.filter(e => e.category === c.slug && e.paid).reduce((s, e) => s + (e.amount_paid || 0), 0));
        return {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#1e1e1e', borderColor: '#2e2e2e',
                textStyle: { color: '#e8e8e8', fontSize: 12 },
                formatter: params => {
                    const lines = params.map(p => `<span style="color:${p.color}">■</span> ${p.seriesName}: <b>${p.value.toLocaleString('uk-UA')} ₴</b>`);
                    return `<b>${params[0].name}</b><br/>${lines.join('<br/>')}`;
                },
            },
            legend: { data: ['Загалом', 'Сплачено'], textStyle: { color: '#888', fontSize: 12 }, top: 4 },
            grid: { left: 16, right: 16, bottom: 8, top: 36, containLabel: true },
            xAxis: {
                type: 'category',
                data: cats.map(c => `${c.icon} ${c.name}`),
                axisLine: { lineStyle: { color: '#2e2e2e' } },
                axisLabel: { color: '#666', fontSize: 11 },
            },
            yAxis: {
                type: 'value',
                axisLine: { lineStyle: { color: '#2e2e2e' } },
                splitLine: { lineStyle: { color: '#1e1e1e' } },
                axisLabel: { color: '#444', fontSize: 10 },
            },
            series: [
                { name: 'Загалом',  type: 'bar', barMaxWidth: 32, data: totalData, itemStyle: { color: '#5865f2', borderRadius: [4,4,0,0] }, emphasis: { focus: 'series' } },
                { name: 'Сплачено', type: 'bar', barMaxWidth: 32, data: paidData,  itemStyle: { color: '#27ae60', borderRadius: [4,4,0,0] }, emphasis: { focus: 'series' } },
            ],
        };
    }, [data, categories]);

    const getCatObj = slug =>
        categories.find(c => c.slug === slug) || { name: slug, slug, color: '#5865f2', icon: '📦' };

    return (
        <div style={{ padding: '28px 32px 200px', width: '100%', boxSizing: 'border-box', minHeight: '100vh', background: '#111' }}>
            <div style={{ marginBottom: 24 }}>
                <div style={{ color: '#e8e8e8', fontWeight: 700, fontSize: 22 }}>Витрати</div>
                <div style={{ color: '#444', fontSize: 13, marginTop: 2 }}>Щомісячні витрати та платежі</div>
            </div>

            {loading ? (
                <div style={{ color: '#444', fontSize: 14, padding: '40px 0' }}>Завантаження…</div>
            ) : (
                <>
                    {/* Summary cards */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                        <SummaryCard label="Гаманець" value={data?.wallet ?? 0} sub={`Зарплата за ${month === 1 ? MONTH_NAMES[12] + ' ' + (year - 1) : MONTH_NAMES[month - 1] + ' ' + year}`} color="#e8e8e8" />
                        <SummaryCard label="Сплачено" value={data?.paid_amount ?? 0} sub={`Прогнозовані витрати: ${(data?.total_amount ?? 0).toLocaleString('uk-UA')} ₴`} color="#e67e22" />
                        <SummaryCard
                            label="Залишок"
                            value={data?.balance ?? 0}
                            sub="Після сплачених витрат"
                            color={(data?.balance ?? 0) >= 0 ? '#2ecc71' : '#e74c3c'}
                            highlight={(data?.balance ?? 0) >= 0 ? '#1e3a1e' : '#3a1e1e'}
                        />
                    </div>

                    {/* Donut / range chart */}
                    <DonutSection expenses={data?.expenses} categories={categories} />

                    {/* Quick one-time expense */}
                    {categories.length > 0 && (
                        <QuickExpenseForm categories={categories} onSave={handleQuickSave} onCategoryCreated={handleCatCreated} />
                    )}

                    {/* Category blocks */}
                    {categories.length > 0 && (
                        <>
                            <input
                                style={{
                                    width: '100%', background: '#111', border: '1px solid #2e2e2e',
                                    borderRadius: 8, color: '#e8e8e8', fontSize: 13, padding: '8px 14px',
                                    outline: 'none', boxSizing: 'border-box', marginBottom: 10,
                                }}
                                placeholder="Пошук категорії…"
                                value={catSearch}
                                onChange={e => setCatSearch(e.target.value)}
                            />
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 20, alignItems: 'start' }}>
                                {categories
                                    .filter(c => c.name.toLowerCase().includes(catSearch.toLowerCase()))
                                    .map(cat => (
                                        <CategoryBlock
                                            key={cat.slug}
                                            catObj={cat}
                                            expenses={data?.expenses}
                                            month={month}
                                            year={year}
                                            onTogglePaid={handleTogglePaid}
                                            onEdit={setEditExp}
                                            onDelete={handleDelete}
                                            onCatUpdated={handleCatUpdated}
                                        />
                                    ))}
                            </div>
                        </>
                    )}

                    {/* Payment calendar */}
                    <div style={{ marginBottom: 20 }}>
                        <PaymentCalendar month={month} year={year} expenses={data?.expenses} onChange={handleMonthChange} />
                    </div>

                    {/* Bar chart — current month */}
                    {barChartOption && (
                        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                            <div style={{ color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
                                По категоріях — {MONTH_NAMES[month]} {year}
                            </div>
                            <ReactECharts option={barChartOption} style={{ height: 220 }} />
                        </div>
                    )}

                    {/* Expenses list */}
                    {data?.expenses?.length > 0 && (
                        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                            <div style={{ color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>
                                Всі витрати
                            </div>
                            <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 2 }}>
                                {[...data.expenses]
                                    .sort((a, b) => {
                                        if (a.paid !== b.paid) return a.paid ? -1 : 1;
                                        return a.day_of_month - b.day_of_month;
                                    })
                                    .map(exp => {
                                        const cat = getCatObj(exp.category);
                                        return (
                                            <div key={exp.id} style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '8px 12px', borderRadius: 8,
                                                background: exp.paid ? '#1a261a' : '#161616',
                                                border: `1px solid ${exp.paid ? '#263626' : '#202020'}`,
                                            }}>
                                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: exp.color || '#5865f2', flexShrink: 0 }} />
                                                <span style={{
                                                    flex: 1, fontSize: 13, fontWeight: 500,
                                                    color: exp.paid ? '#4a6a4a' : '#c8c8c8',
                                                    textDecoration: exp.paid ? 'line-through' : 'none',
                                                }}>{exp.title}</span>
                                                <span style={{ fontSize: 11, color: '#555', background: '#111', padding: '2px 8px', borderRadius: 10 }}>
                                                {cat.icon} {cat.name}
                                            </span>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: exp.paid ? '#4a6a4a' : '#e8e8e8', minWidth: 70, textAlign: 'right' }}>
                                                {exp.amount.toLocaleString('uk-UA')} ₴
                                            </span>
                                                <span style={{ fontSize: 11, color: '#444', minWidth: 40 }}>{exp.day_of_month} ч.</span>
                                                <span style={{
                                                    fontSize: 11, padding: '2px 8px', borderRadius: 10,
                                                    background: exp.paid ? '#1e3a1e' : '#1e1e1e',
                                                    color: exp.paid ? '#5abf5a' : '#444',
                                                    border: `1px solid ${exp.paid ? '#2a5a2a' : '#2a2a2a'}`,
                                                }}>
                                                {exp.paid ? 'Сплачено' : 'Не сплачено'}
                                            </span>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}

                    {/* Expense form */}
                    {categories.length > 0 && (
                        <ExpenseForm
                            initial={editExp ? {
                                title:         editExp.title,
                                amount:        editExp.amount,
                                color:         editExp.color,
                                category:      editExp.category,
                                day_of_month:  editExp.day_of_month,
                                repeat_until:  editExp.repeat_until || '',
                                notify_before: editExp.notify_before,
                            } : null}
                            onSave={handleSave}
                            onCancel={() => setEditExp(null)}
                            categories={categories}
                            onCategoryCreated={handleCatCreated}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ExpensesPage;
